/**
 * TDBCostIntelligence — Aditya's Food Cost + General Cost + Invoice Scanner.
 *
 * Layout (per Dipali's Apr-22 spec):
 *   Alerts strip (price spikes · recipe spikes · food-cost breaches · silent cities)
 *   Tab strip: Food Cost  ·  General Cost  ·  Scan Invoice
 *
 *   Food Cost tab — 3 city cards (Bengaluru / Mumbai / Gurgaon) with food
 *   cost % badge, category breakdown on click, 6-month trend chart per city,
 *   ingredient intelligence table.
 *
 *   General Cost tab — 3 city cards for non-food spend, category × city
 *   matrix, per-city drill-down.
 *
 *   Scan Invoice tab — upload → auto-classify line items → Aditya flips as
 *   needed → Confirm → saves to the right collections → city cards refresh.
 *
 * Reads:
 *   GET /api/tdb/food-cost/cities         (3-card dashboard)
 *   GET /api/tdb/food-cost/city/:name     (food drill-down)
 *   GET /api/tdb/food-cost/ingredients    (price intelligence table)
 *   GET /api/tdb/food-cost/trend          (6-month chart)
 *   GET /api/tdb/general-cost/cities      (non-food 3-card dashboard)
 *   GET /api/tdb/general-cost/matrix      (category × city matrix)
 *   GET /api/tdb/general-cost/city/:name  (non-food drill-down)
 *   GET /api/tdb/alerts/active            (all active cost alerts)
 * Writes:
 *   POST /api/tdb/invoice/scan           (Gemini extraction + classification)
 *   POST /api/tdb/invoice/save-split     (writes to food + general collections)
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import {
  Upload, MapPin, Utensils, Package, Flame, AlertTriangle, CheckCircle2,
  Loader2, ChevronRight, X, Bell,
} from 'lucide-react';

const INR = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const LAKH = (n) => n >= 100000 ? `₹${(n / 1e5).toFixed(2)}L` : INR(n);

const CITY_COLORS = {
  Bengaluru: '#2A7B88',
  Mumbai: '#BA7517',
  Gurgaon: '#D4726A',
};

export default function TDBCostIntelligence() {
  const [tab, setTab] = useState('food');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);
  const [ingredients, setIngredients] = useState([]);
  const [trend, setTrend] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, ig, tr] = await Promise.all([
        api.get(`/tdb/food-cost/cities?month=${month}`),
        api.get(`/tdb/food-cost/ingredients?limit=30`),
        api.get(`/tdb/food-cost/trend?months=6`),
      ]);
      setCities(c.data?.cities || []);
      setIngredients(ig.data?.items || []);
      setTrend(tr.data || null);
    } catch {
      // Silent fail for now; dashboard shows empty state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  return (
    <div className="space-y-4" data-testid="tdb-cost-intelligence">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1B2A4A' }}>Cost Intelligence</h2>
          <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Scan invoices → auto-classify → food cost % per city, live.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 className="border rounded-md px-2 py-1.5 text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="month-picker" />
          <button onClick={() => setTab('scan')}
                  className="px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5"
                  style={{ backgroundColor: '#2A7B88' }} data-testid="scan-invoice-btn">
            <Upload size={14} /> Scan Invoice
          </button>
        </div>
      </div>

      {/* Active alerts strip — polls /alerts/active so Aditya sees price
          spikes, recipe spikes, food-cost breaches and silent-city nudges. */}
      <AlertsStrip onCityClick={(city) => setTab('food')} />

      {/* Tab bar */}
      <div className="flex gap-1 border-b" style={{ borderColor: '#E5E1DC' }}>
        {[
          { id: 'food', label: 'Food Cost', icon: Utensils },
          { id: 'general', label: 'General Cost', icon: Package },
          { id: 'scan', label: 'Scan Invoice', icon: Upload },
        ].map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                    className="px-4 py-2 text-xs font-semibold flex items-center gap-1.5 border-b-2 -mb-px"
                    style={{ borderColor: active ? '#2A7B88' : 'transparent',
                             color: active ? '#2A7B88' : '#6B7280' }}
                    data-testid={`cost-tab-${t.id}`}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="mx-auto animate-spin" size={20} style={{ color: '#6B7280' }} /></div>}

      {!loading && tab === 'food' && (
        <FoodCostView
          cities={cities} ingredients={ingredients} trend={trend}
          selectedCity={selectedCity} onSelectCity={setSelectedCity}
          month={month}
        />
      )}

      {!loading && tab === 'general' && (
        <GeneralCostView month={month} />
      )}

      {tab === 'scan' && (
        <InvoiceScanner onDone={() => { setTab('food'); load(); }} />
      )}
    </div>
  );
}

// ─── Alerts strip ──────────────────────────────────────────────────────────
function AlertsStrip({ onCityClick }) {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchAlerts = () => {
      api.get('/tdb/alerts/active')
        .then(r => { if (alive) setAlerts(r.data?.items || []); })
        .catch(() => { if (alive) setAlerts([]); });
    };
    fetchAlerts();
    const t = setInterval(fetchAlerts, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (!alerts.length) return null;

  const red = alerts.filter(a => a.level === 'red' || a.kind === 'price_spike' || a.kind === 'recipe_spike').length;
  const amber = alerts.length - red;

  return (
    <div className="rounded-lg border overflow-hidden" style={{ borderColor: '#F3D5A9', backgroundColor: '#FFFBEB' }} data-testid="tdb-alerts-strip">
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
        data-testid="toggle-alerts-strip">
        <div className="flex items-center gap-2">
          <Bell size={14} style={{ color: '#BA7517' }} />
          <p className="text-xs font-semibold" style={{ color: '#78350F' }}>
            {alerts.length} active cost alert{alerts.length === 1 ? '' : 's'}
            {red > 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold text-white" style={{ backgroundColor: '#D4726A' }}>{red} RED</span>}
            {amber > 0 && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>{amber} AMBER</span>}
          </p>
        </div>
        <span className="text-[11px] font-semibold" style={{ color: '#2A7B88' }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-1.5 max-h-60 overflow-y-auto">
          {alerts.slice(0, 12).map((a, i) => (
            <AlertRow key={a.id || i} alert={a} onCityClick={onCityClick} />
          ))}
          {alerts.length > 12 && (
            <p className="text-[10px] text-center italic pt-1" style={{ color: '#9CA3AF' }}>
              +{alerts.length - 12} more alerts this month
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function AlertRow({ alert, onCityClick }) {
  const level = alert.level || (alert.kind === 'silent_city' ? 'amber' : 'red');
  const col = level === 'red'
    ? { bg: '#FEF2F2', fg: '#991B1B', border: '#D4726A', tag: 'RED' }
    : { bg: '#FFFBEB', fg: '#78350F', border: '#BA7517', tag: 'AMBER' };

  let label = '';
  if (alert.kind === 'price_spike') {
    label = `Ingredient spike: ${alert.item_name} · +${alert.spike_pct}% above 3-mo avg (₹${Math.round(alert.new_price)}/${alert.unit || 'unit'}, ${alert.city || 'any city'})`;
  } else if (alert.kind === 'recipe_spike') {
    label = `Recipe over 15%: ${alert.product_title || alert.product_name} · food cost now ${alert.food_cost_pct}%`;
  } else if (alert.kind === 'silent_city') {
    label = `${alert.city} silent for ${alert.days_silent}+ days — no invoices booked`;
  } else {
    // Food cost threshold alert (level + food_cost_pct + city + threshold)
    label = `${alert.city || 'TDB'} food cost ${alert.food_cost_pct}% — above ${alert.threshold}% threshold`;
  }

  return (
    <div
      onClick={() => alert.city && onCityClick?.(alert.city)}
      className="flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer"
      style={{ backgroundColor: col.bg, borderLeft: `3px solid ${col.border}` }}
      data-testid={`alert-row-${alert.kind || 'threshold'}`}>
      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 mt-0.5"
        style={{ backgroundColor: col.border, color: 'white' }}>{col.tag}</span>
      <p className="text-[11px] leading-tight flex-1" style={{ color: col.fg }}>{label}</p>
    </div>
  );
}

// ─── Food Cost View (3 cities + drill + trend + ingredients) ────────────
function FoodCostView({ cities, ingredients, trend, selectedCity, onSelectCity, month }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cities.map(c => (
          <CityCard key={c.city} city={c}
            onClick={() => onSelectCity(c)}
            isSelected={selectedCity?.city === c.city}
            metricLabel="Food cost"
            metricPct={c.food_cost_pct}
            level={c.level}
            targetPct={c.target_pct}
            headline={c.level === 'no_data' ? '—' : `${c.food_cost_pct}%`}
            rows={[
              { label: 'This month', value: LAKH(c.food_cost) },
              { label: 'Revenue', value: LAKH(c.revenue) },
              { label: 'Invoices', value: c.invoice_count },
            ]}
            kind="food" />
        ))}
      </div>

      {selectedCity && (
        <CityDrilldown city={selectedCity.city} month={month} onClose={() => onSelectCity(null)} />
      )}

      {/* 6-month trend chart */}
      {trend && trend.series?.length > 0 && (
        <TrendChart trend={trend} />
      )}

      {/* Ingredient intelligence */}
      <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="ingredient-intelligence">
        <h3 className="text-sm font-bold mb-2" style={{ color: '#1B2A4A' }}>Ingredient Intelligence</h3>
        <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
          Latest prices, trends and vendors for every ingredient. Sorted by volume. Spikes (&gt;10% vs avg) flagged in red.
        </p>
        {ingredients.length === 0 ? (
          <div className="py-6 text-center text-xs" style={{ color: '#9CA3AF' }}>No ingredient purchases yet. Scan your first invoice.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }} className="text-left">
                  <th className="px-2 py-2 font-semibold">Item</th>
                  <th className="px-2 py-2 font-semibold text-right">Qty</th>
                  <th className="px-2 py-2 font-semibold text-right">Last ₹</th>
                  <th className="px-2 py-2 font-semibold text-right">Avg ₹</th>
                  <th className="px-2 py-2 font-semibold text-right">Trend</th>
                  <th className="px-2 py-2 font-semibold">Vendors</th>
                </tr>
              </thead>
              <tbody>
                {ingredients.map((it, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                    <td className="px-2 py-1.5 font-medium" style={{ color: '#1B2A4A' }}>{it.item_name}</td>
                    <td className="px-2 py-1.5 text-right" style={{ color: '#6B7280' }}>{it.total_qty}{it.unit ? ` ${it.unit}` : ''}</td>
                    <td className="px-2 py-1.5 text-right">{INR(it.last_unit_price)}</td>
                    <td className="px-2 py-1.5 text-right" style={{ color: '#6B7280' }}>{INR(it.avg_unit_price)}</td>
                    <td className="px-2 py-1.5 text-right font-bold" style={{ color: it.trend === 'up' ? '#D4726A' : it.trend === 'down' ? '#2D8B5E' : '#6B7280' }}>
                      {it.trend === 'up' ? '▲' : it.trend === 'down' ? '▼' : '■'} {it.price_diff_pct}%
                    </td>
                    <td className="px-2 py-1.5 text-[10px]" style={{ color: '#6B7280' }}>{(it.vendors || []).join(', ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// Reusable city card used by both Food and General views
function CityCard({ city, onClick, isSelected, metricLabel, metricPct, level, targetPct, headline, rows, kind }) {
  const colors = {
    green:   { bg: '#F0FDF4', border: '#2D8B5E', text: '#14532D', emoji: '✅' },
    amber:   { bg: '#FFFBEB', border: '#BA7517', text: '#78350F', emoji: '⚠️' },
    red:     { bg: '#FEF2F2', border: '#D4726A', text: '#991B1B', emoji: '🔴' },
    no_data: { bg: '#F7F5F2', border: '#E5E1DC', text: '#6B7280', emoji: '—' },
  }[level] || { bg: '#F7F5F2', border: '#E5E1DC', text: '#6B7280', emoji: '—' };

  return (
    <button onClick={onClick}
      className="p-4 rounded-lg border-2 text-left hover:shadow-md transition"
      style={{ backgroundColor: colors.bg, borderColor: isSelected ? '#2A7B88' : colors.border, borderWidth: isSelected ? 3 : 2 }}
      data-testid={`city-card-${kind}-${city.city}`}>
      <div className="flex items-center gap-2 mb-2">
        <MapPin size={14} style={{ color: colors.text }} />
        <span className="text-sm font-bold uppercase tracking-wide" style={{ color: colors.text }}>{city.city}</span>
        <span className="ml-auto text-lg">{colors.emoji}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: colors.text }}>{headline}</p>
      <p className="text-[10px] uppercase tracking-wide" style={{ color: colors.text }}>
        {metricLabel}{targetPct ? ` · target ${targetPct}%` : ''}
      </p>
      <div className="mt-2 pt-2 border-t text-[11px] space-y-0.5" style={{ borderColor: colors.border, color: colors.text }}>
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between">
            <span>{r.label}</span><span className="font-semibold">{r.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-1 text-[10px] flex items-center gap-1" style={{ color: colors.text }}>
        <ChevronRight size={11} /> Click to drill in
      </div>
    </button>
  );
}

function CityDrilldown({ city, month, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/tdb/food-cost/city/${encodeURIComponent(city)}?month=${month}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail({ by_category: [], invoices: [] }));
  }, [city, month]);

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#2A7B88' }} data-testid="city-drilldown">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{city} · {month} · Food Cost Breakdown</h3>
        <button onClick={onClose} data-testid="close-drilldown"><X size={14} /></button>
      </div>
      {!detail ? (
        <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin" size={18} style={{ color: '#6B7280' }} /></div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>By Category</p>
            {detail.by_category.length === 0 ? (
              <p className="text-xs italic" style={{ color: '#9CA3AF' }}>No food cost entries yet.</p>
            ) : (
              <div className="space-y-1">
                {detail.by_category.map(c => (
                  <div key={c.category} className="flex items-center justify-between px-2 py-1.5 rounded" style={{ backgroundColor: '#FAFAF7' }}>
                    <span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{c.category}</span>
                    <span className="text-xs font-bold" style={{ color: '#2A7B88' }}>{INR(c.total)} · {c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#6B7280' }}>Invoices</p>
            {detail.invoices.length === 0 ? (
              <p className="text-xs italic" style={{ color: '#9CA3AF' }}>No invoices yet.</p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {detail.invoices.map(inv => (
                  <div key={inv.id} className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: '#E5E1DC' }}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium" style={{ color: '#1B2A4A' }}>{inv.vendor_name || 'Unknown'}</span>
                      <span style={{ color: '#2A7B88' }} className="font-bold">{INR(inv.grand_total || inv.food_total)}</span>
                    </div>
                    <div className="flex items-center justify-between text-[10px] mt-0.5" style={{ color: '#9CA3AF' }}>
                      <span>{inv.invoice_number || '—'}</span>
                      <span>{inv.invoice_date}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 6-month Trend Chart (inline SVG, no chart lib) ──────────────────────
function TrendChart({ trend }) {
  const series = trend.series || [];
  const cities = trend.cities || [];
  const target = trend.target_pct || 15;
  if (!series.length) return null;

  const W = 640, H = 200, PAD_L = 32, PAD_R = 12, PAD_T = 16, PAD_B = 28;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  // Y axis = 0..max%
  const allPcts = series.flatMap(s => cities.map(c => Number(s[c] || 0)));
  const yMax = Math.max(30, Math.ceil(Math.max(...allPcts, target + 5) / 5) * 5);

  const x = (i) => PAD_L + (series.length === 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
  const y = (pct) => PAD_T + innerH - (pct / yMax) * innerH;

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="food-cost-trend-chart">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>Food Cost % · 6-month trend</h3>
        <div className="flex items-center gap-3 text-[10px]" style={{ color: '#6B7280' }}>
          {cities.map(c => (
            <span key={c} className="inline-flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CITY_COLORS[c] || '#6B7280' }} /> {c}
            </span>
          ))}
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-[2px]" style={{ backgroundColor: '#2D8B5E' }} /> Target {target}%
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMinYMin meet">
        {/* Y gridlines */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const v = Math.round(yMax * f);
          return (
            <g key={i}>
              <line x1={PAD_L} x2={W - PAD_R} y1={y(v)} y2={y(v)} stroke="#F3F4F6" strokeWidth="1" />
              <text x={PAD_L - 4} y={y(v) + 3} textAnchor="end" fontSize="9" fill="#9CA3AF">{v}%</text>
            </g>
          );
        })}
        {/* Target line */}
        <line x1={PAD_L} x2={W - PAD_R} y1={y(target)} y2={y(target)}
              stroke="#2D8B5E" strokeWidth="1.2" strokeDasharray="4,3" />
        {/* X labels */}
        {series.map((s, i) => (
          <text key={s.month} x={x(i)} y={H - PAD_B + 14}
                textAnchor="middle" fontSize="9" fill="#6B7280">{shortMonth(s.month)}</text>
        ))}
        {/* Lines + dots */}
        {cities.map(c => {
          const color = CITY_COLORS[c] || '#6B7280';
          const pts = series.map((s, i) => `${x(i)},${y(Number(s[c] || 0))}`).join(' ');
          return (
            <g key={c}>
              <polyline points={pts} fill="none" stroke={color} strokeWidth="2" />
              {series.map((s, i) => (
                <circle key={i} cx={x(i)} cy={y(Number(s[c] || 0))} r="3" fill={color}>
                  <title>{`${c} · ${s.month} · ${Number(s[c] || 0).toFixed(1)}%`}</title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function shortMonth(ym) {
  if (!ym || ym.length !== 7) return ym;
  const [y, m] = ym.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString('en-US', { month: 'short' });
}

// ─── General Cost View — 3 cities + matrix + drill ──────────────────────
function GeneralCostView({ month }) {
  const [cities, setCities] = useState([]);
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      api.get(`/tdb/general-cost/cities?month=${month}`),
      api.get(`/tdb/general-cost/matrix?month=${month}`),
    ]).then(([c, m]) => {
      if (!alive) return;
      setCities(c.data?.cities || []);
      setMatrix(m.data || null);
    }).catch(() => {
      if (!alive) return;
      setCities([]);
      setMatrix(null);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [month]);

  if (loading) return <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin" size={18} style={{ color: '#6B7280' }} /></div>;

  return (
    <div className="space-y-4" data-testid="general-cost-view">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cities.map(c => (
          <CityCard key={c.city} city={c}
            onClick={() => setSelectedCity(c)}
            isSelected={selectedCity?.city === c.city}
            metricLabel="Non-food spend"
            level={c.invoice_count > 0 ? 'amber' : 'no_data'}
            headline={c.invoice_count === 0 ? '—' : LAKH(c.general_cost)}
            rows={[
              { label: 'Non-food cost', value: LAKH(c.general_cost) },
              { label: 'As % of revenue', value: c.revenue > 0 ? `${c.general_cost_pct}%` : '—' },
              { label: 'Invoices', value: c.invoice_count },
            ]}
            kind="general" />
        ))}
      </div>

      {selectedCity && (
        <GeneralCityDrilldown city={selectedCity.city} month={month} onClose={() => setSelectedCity(null)} />
      )}

      {/* Category × City matrix */}
      {matrix && matrix.rows?.length > 0 ? (
        <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#E5E1DC' }} data-testid="general-cost-matrix">
          <h3 className="text-sm font-bold mb-2" style={{ color: '#1B2A4A' }}>Category × City Matrix · {month}</h3>
          <p className="text-[11px] mb-3" style={{ color: '#6B7280' }}>
            Non-food spend broken down by category. Click any cell to drill into that city.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ backgroundColor: '#F7F5F2' }}>
                  <th className="px-2 py-2 font-semibold text-left">Category</th>
                  {matrix.cities.map(c => (
                    <th key={c} className="px-2 py-2 font-semibold text-right">{c}</th>
                  ))}
                  <th className="px-2 py-2 font-semibold text-right" style={{ backgroundColor: '#E5E1DC' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {matrix.rows.map(r => (
                  <tr key={r.category} className="border-t" style={{ borderColor: '#F3F4F6' }}>
                    <td className="px-2 py-1.5 font-medium" style={{ color: '#1B2A4A' }}>{r.category}</td>
                    {matrix.cities.map(c => (
                      <td key={c} className="px-2 py-1.5 text-right"
                          style={{ color: r.totals_by_city[c] > 0 ? '#1B2A4A' : '#D1D5DB' }}>
                        {r.totals_by_city[c] > 0 ? INR(r.totals_by_city[c]) : '—'}
                      </td>
                    ))}
                    <td className="px-2 py-1.5 text-right font-bold" style={{ color: '#2A7B88' }}>{INR(r.row_total)}</td>
                  </tr>
                ))}
                <tr className="border-t" style={{ backgroundColor: '#FAFAF7', borderColor: '#E5E1DC' }}>
                  <td className="px-2 py-2 font-bold" style={{ color: '#1B2A4A' }}>Total</td>
                  {matrix.cities.map(c => (
                    <td key={c} className="px-2 py-2 text-right font-bold" style={{ color: '#1B2A4A' }}>
                      {INR(matrix.col_totals?.[c] || 0)}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right font-extrabold" style={{ color: '#2A7B88' }}>{INR(matrix.grand_total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border p-6 text-center" style={{ borderColor: '#E5E1DC' }}>
          <Package size={28} className="mx-auto mb-2" style={{ color: '#BA7517' }} />
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>No non-food spend recorded for {month}</p>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Scan an invoice with packaging, cleaning, rent or utility items to populate this view.</p>
        </div>
      )}
    </div>
  );
}

function GeneralCityDrilldown({ city, month, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get(`/tdb/general-cost/city/${encodeURIComponent(city)}?month=${month}`)
      .then(r => setDetail(r.data))
      .catch(() => setDetail({ by_category: [] }));
  }, [city, month]);

  return (
    <div className="bg-white rounded-lg border p-4" style={{ borderColor: '#2A7B88' }} data-testid="general-city-drilldown">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{city} · {month} · Non-food Breakdown</h3>
        <button onClick={onClose} data-testid="close-general-drilldown"><X size={14} /></button>
      </div>
      {!detail ? (
        <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin" size={18} style={{ color: '#6B7280' }} /></div>
      ) : detail.by_category.length === 0 ? (
        <p className="text-xs italic text-center py-4" style={{ color: '#9CA3AF' }}>No non-food entries yet for this city.</p>
      ) : (
        <div className="space-y-1">
          {detail.by_category.map(c => (
            <div key={c.category} className="flex items-center justify-between px-2 py-1.5 rounded" style={{ backgroundColor: '#FAFAF7' }}>
              <span className="text-xs font-medium" style={{ color: '#1B2A4A' }}>{c.category}</span>
              <span className="text-xs font-bold" style={{ color: '#BA7517' }}>{INR(c.total)} · {c.count} {c.count === 1 ? 'line' : 'lines'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Invoice Scanner (upload → classify → flip → confirm) ──────────────────
function InvoiceScanner({ onDone }) {
  const [file, setFile] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [city, setCity] = useState('Bengaluru');
  const [error, setError] = useState('');
  const [alertMsg, setAlertMsg] = useState(null);

  const scan = async () => {
    if (!file) return;
    setScanning(true); setError(''); setAlertMsg(null);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await api.post('/tdb/invoice/scan', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setExtracted(r.data?.extracted);
      if (r.data?.extracted?.detected_city) {
        setCity(r.data.extracted.detected_city);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Scan failed. Please try again.');
    } finally { setScanning(false); }
  };

  const flip = (idx) => {
    setExtracted(prev => {
      const next = { ...prev };
      next.line_items = prev.line_items.map((it, i) => {
        if (i !== idx) return it;
        const current = it.classification || 'unclassified';
        const flipped = current === 'food' ? 'non_food' : current === 'non_food' ? 'food' : 'food';
        return { ...it, classification: flipped };
      });
      return next;
    });
  };

  const save = async () => {
    if (!extracted) return;
    setSaving(true); setError('');
    try {
      const payload = {
        ...extracted,
        matched_vendor_id: extracted.matched_vendor?.id || '',
        city,
      };
      const r = await api.post('/tdb/invoice/save-split', payload);
      if (r.data?.duplicate) {
        setError(r.data?.message || 'This invoice is already saved. Delete the earlier copy first.');
        return;
      }
      if (r.data?.alert) {
        setAlertMsg(r.data.alert);
      } else {
        onDone?.();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (alertMsg) {
    return (
      <div className="bg-white rounded-lg border-2 p-5 text-center" style={{ borderColor: alertMsg.level === 'red' ? '#D4726A' : '#BA7517' }} data-testid="invoice-post-save-alert">
        <AlertTriangle size={30} className="mx-auto mb-2" style={{ color: alertMsg.level === 'red' ? '#D4726A' : '#BA7517' }} />
        <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>
          {city} food cost is now {alertMsg.food_cost_pct}% — {alertMsg.level === 'red' ? 'ABOVE 20% (RED)' : 'ABOVE 15% (AMBER)'}
        </p>
        <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>Threshold: {alertMsg.threshold}%. Review {city} vendor prices or push online channel.</p>
        <button onClick={onDone} className="mt-3 px-4 py-2 rounded-md text-xs font-semibold text-white" style={{ backgroundColor: '#2A7B88' }} data-testid="view-dashboard-after-alert">View dashboard</button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border p-5" style={{ borderColor: '#E5E1DC' }}>
      {!extracted && (
        <div className="text-center">
          <Upload size={24} className="mx-auto mb-2" style={{ color: '#2A7B88' }} />
          <p className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Upload a supplier invoice</p>
          <p className="text-[11px] mt-1 mb-3" style={{ color: '#6B7280' }}>JPG, PNG, WEBP or PDF · Gemini reads line items + auto-classifies food vs non-food</p>
          <label className="inline-block">
            <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="text-xs" data-testid="invoice-file-input" />
          </label>
          {file && (
            <div className="mt-3 space-y-2 max-w-xs mx-auto">
              <p className="text-xs" style={{ color: '#6B7280' }}>{file.name}</p>
              <select value={city} onChange={(e) => setCity(e.target.value)}
                      className="w-full border rounded-md px-2 py-1.5 text-xs" style={{ borderColor: '#E5E1DC' }} data-testid="scan-city">
                <option>Bengaluru</option><option>Mumbai</option><option>Gurgaon</option>
              </select>
              <button onClick={scan} disabled={scanning}
                      className="w-full px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 justify-center"
                      style={{ backgroundColor: '#2A7B88' }} data-testid="scan-go-btn">
                {scanning ? <><Loader2 size={12} className="animate-spin" /> Scanning with Gemini…</> : <><Flame size={12} /> Scan invoice</>}
              </button>
            </div>
          )}
          {error && <p className="mt-2 text-xs" style={{ color: '#D4726A' }}>{error}</p>}
        </div>
      )}

      {extracted && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm font-bold" style={{ color: '#1B2A4A' }}>{extracted.vendor_name || 'Unknown vendor'}</p>
              <p className="text-[11px]" style={{ color: '#6B7280' }}>
                {extracted.invoice_number || '—'} · {extracted.invoice_date || '—'} · {INR(extracted.grand_total)} · {city}
              </p>
              {extracted.classification_summary && (
                <p className="text-[11px] mt-1" style={{ color: '#2A7B88' }}>
                  {extracted.classification_summary.food_count} food items ({INR(extracted.classification_summary.food_total)}) ·{' '}
                  {extracted.classification_summary.non_food_count} non-food ({INR(extracted.classification_summary.non_food_total)})
                  {extracted.classification_summary.unclassified_count > 0 && ` · ${extracted.classification_summary.unclassified_count} unclassified`}
                </p>
              )}
            </div>
            <button onClick={() => setExtracted(null)} className="text-xs" style={{ color: '#6B7280' }}>Cancel</button>
          </div>

          <div className="space-y-1 max-h-96 overflow-y-auto mb-3">
            {(extracted.line_items || []).map((it, i) => {
              const cls = it.classification || 'unclassified';
              const tag = cls === 'food' ? { bg: '#DCFCE7', tx: '#14532D', label: 'FOOD' }
                       : cls === 'non_food' ? { bg: '#FEF3C7', tx: '#78350F', label: 'NON-FOOD' }
                       : { bg: '#FEF2F2', tx: '#991B1B', label: 'UNTAGGED · TAP' };
              return (
                <div key={i} className="flex items-center gap-2 px-2 py-1.5 rounded border" style={{ borderColor: '#E5E1DC' }}>
                  <button onClick={() => flip(i)} data-testid={`flip-item-${i}`}
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: tag.bg, color: tag.tx }}>
                    {tag.label}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: '#1B2A4A' }}>{it.item_name || it.description}</p>
                    <p className="text-[10px]" style={{ color: '#9CA3AF' }}>
                      {it.quantity || 0} {it.unit || ''} × ₹{it.unit_price || 0}
                    </p>
                  </div>
                  <span className="text-xs font-bold flex-shrink-0" style={{ color: '#2A7B88' }}>{INR(it.total)}</span>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2">
            <button onClick={() => setExtracted(null)} className="px-3 py-2 text-xs rounded-md border" style={{ borderColor: '#E5E1DC', color: '#6B7280' }}>Discard</button>
            <button onClick={save} disabled={saving}
                    className="flex-1 px-3 py-2 rounded-md text-xs font-semibold text-white flex items-center gap-1.5 justify-center"
                    style={{ backgroundColor: '#2A7B88' }} data-testid="confirm-invoice-btn">
              {saving ? <><Loader2 size={12} className="animate-spin" /> Saving…</> : <><CheckCircle2 size={12} /> Confirm — split into food + non-food</>}
            </button>
          </div>
          {error && <p className="mt-2 text-xs" style={{ color: '#D4726A' }}>{error}</p>}
        </div>
      )}
    </div>
  );
}
